import { CHAT_LIMITS, type ChatConversationView } from '@mogzo/shared';
import { Bell, BellOff, LogOut, PenLine, UserPlus, X } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { ApiError } from '../../lib/api';
import {
  addToGroup,
  changeChat,
  leaveGroup,
  refreshChatCounts,
  removeFromGroup,
} from '../../lib/chat';
import { Avatar } from '../community/Avatar';
import { Alert, buttonClass } from '../ui';

const added = (iso: string) =>
  `Added ${new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

/**
 * A group's people (M38): whoever made it adds and removes them (up to 10, only people they
 * already chat with) and renames it; anyone can mute it or leave. Beside the chat on a computer,
 * over it on a phone and in the chat window.
 */
export function GroupPanel({
  chat,
  floating,
  onClose,
  onChanged,
  onGone,
}: {
  chat: ChatConversationView;
  floating: boolean;
  onClose: () => void;
  onChanged: () => void;
  onGone: () => void;
}) {
  const [name, setName] = useState('');
  const [renaming, setRenaming] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const full = chat.members.length >= CHAT_LIMITS.groupSize;

  async function run(action: () => Promise<unknown>, then: () => void = onChanged) {
    setBusy(true);
    setError(null);
    try {
      await action();
      refreshChatCounts();
      then();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That didn’t work. Try again.');
    } finally {
      setBusy(false);
    }
  }

  function add(event: FormEvent) {
    event.preventDefault();
    const username = name.trim();
    if (!username) return;
    void run(
      () => addToGroup(chat.id, username),
      () => {
        setName('');
        onChanged();
      },
    );
  }

  const body = (
    <div className="flex flex-col gap-4.5">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-[17px] font-bold">
          {chat.members.length === 1 ? 'Just you' : `${chat.members.length} people`}
        </h2>
        {floating && (
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex size-10 items-center justify-center rounded-full hover:bg-field"
          >
            <X size={20} strokeWidth={1.9} aria-hidden="true" />
          </button>
        )}
      </div>
      <ul className="m-0 flex list-none flex-col p-0">
        {chat.members.map((member) => {
          const you = !chat.people.some((p) => p.id === member.id);
          return (
            <li key={member.id ?? member.username} className="flex min-h-12 items-center gap-2.5">
              <Avatar name={member.username} size="md" />
              <span className="min-w-0 flex-1 leading-[18px]">
                <b className="block truncate text-sm font-semibold">
                  {member.username}
                  {you && <span className="font-normal text-muted"> (you)</span>}
                </b>
                <span className="text-[13px] text-muted">
                  {member.owner ? 'Looks after the group' : added(member.addedAt)}
                </span>
              </span>
              {chat.owner && !you && member.id && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    if (window.confirm(`Remove ${member.username} from ${chat.name}?`)) {
                      void run(() => removeFromGroup(chat.id, member.id as string));
                    }
                  }}
                  className="rounded-full px-3 py-1.5 text-[13px] font-bold text-muted hover:bg-field hover:text-ink"
                >
                  Remove
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {chat.owner && !full && (
        <form onSubmit={add} className="flex gap-2">
          <label htmlFor={`add-${chat.id}`} className="sr-only">
            Username to add
          </label>
          <input
            id={`add-${chat.id}`}
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Username"
            autoComplete="off"
            className="h-10 min-w-0 flex-1 rounded-full border border-line bg-field px-4 text-sm focus:border-ink focus:bg-card focus:outline-none"
          />
          <button
            type="submit"
            disabled={busy || !name.trim()}
            className={buttonClass('secondary', false)}
          >
            <UserPlus size={16} strokeWidth={1.9} aria-hidden="true" />
            Add
          </button>
        </form>
      )}
      <p className="-mt-2 text-[13px] leading-[18px] text-muted">
        A group holds up to {CHAT_LIMITS.groupSize} people. Only someone who already chats with
        whoever made it can be added.
      </p>
      {error && <Alert>{error}</Alert>}
      <div className="-mx-2 flex flex-col gap-0.5 border-t border-line pt-3">
        {chat.owner &&
          (renaming === null ? (
            <button
              type="button"
              onClick={() => setRenaming(chat.name)}
              className="flex h-11 items-center gap-3 rounded-xl px-2.5 text-left font-medium hover:bg-field"
            >
              <PenLine size={18} strokeWidth={1.9} aria-hidden="true" />
              Rename group
            </button>
          ) : (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void run(
                  () => changeChat(chat.id, { name: renaming.trim() }),
                  () => {
                    setRenaming(null);
                    onChanged();
                  },
                );
              }}
              className="flex gap-2 px-2.5 py-1"
            >
              <label htmlFor={`name-${chat.id}`} className="sr-only">
                Group name
              </label>
              <input
                id={`name-${chat.id}`}
                value={renaming}
                maxLength={CHAT_LIMITS.groupName}
                onChange={(event) => setRenaming(event.target.value)}
                className="h-10 min-w-0 flex-1 rounded-full border border-ink bg-card px-4 text-sm focus:outline-none"
              />
              <button
                type="submit"
                disabled={busy || !renaming.trim()}
                className={buttonClass('primary', false)}
              >
                Save
              </button>
            </form>
          ))}
        <button
          type="button"
          onClick={() => run(() => changeChat(chat.id, { muted: !chat.muted }))}
          className="flex h-11 items-center gap-3 rounded-xl px-2.5 text-left font-medium hover:bg-field"
        >
          {chat.muted ? (
            <Bell size={18} strokeWidth={1.9} aria-hidden="true" />
          ) : (
            <BellOff size={18} strokeWidth={1.9} aria-hidden="true" />
          )}
          {chat.muted ? 'Unmute this group' : 'Mute this group'}
        </button>
        <button
          type="button"
          onClick={() => {
            if (window.confirm(`Leave ${chat.name}?`)) void run(() => leaveGroup(chat.id), onGone);
          }}
          className="flex h-11 items-center gap-3 rounded-xl px-2.5 text-left font-medium text-[#c23a32] hover:bg-field"
        >
          <LogOut size={18} strokeWidth={1.9} aria-hidden="true" />
          Leave group
        </button>
      </div>
    </div>
  );

  if (floating) {
    return (
      <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/45 sm:items-center sm:p-4">
        <section
          aria-label={`People in ${chat.name}`}
          className="max-h-[90dvh] w-full max-w-[420px] overflow-y-auto rounded-t-[22px] bg-card p-5 sm:rounded-[22px]"
        >
          {body}
        </section>
      </div>
    );
  }
  return (
    <aside
      aria-label={`People in ${chat.name}`}
      className="hidden w-[300px] shrink-0 overflow-y-auto border-l border-line bg-card px-[18px] py-5 lg:block"
    >
      {body}
    </aside>
  );
}
